/* The frame around every page: top bar, footer, and the account section's sidebar. */
import { useEffect, type ReactNode } from 'react';
import { LANGS, LANG_NAMES, WEB_ROUTES, asLang, langDir, type Lang } from '@pathabroad/core/browser';
import { BrandMark, Icon } from './icons';
import { Link, useLocation } from './nav';
import { useT } from './i18n';
import { useSession } from './session';
import type { MessageKey } from '../lib/i18n';
import { Row, type Tone } from './ui';
import { isNightPath } from '../lib/routes';

/* the home page's sections, linked from the top bar on wide screens */
export const LANDING_SECTIONS: Array<{ id: string; key: MessageKey }> = [
  { id: 'money', key: 'land.nav.money' },
  { id: 'how', key: 'land.nav.how' },
  { id: 'features', key: 'land.nav.features' },
  { id: 'trust', key: 'land.nav.trust' },
  { id: 'pricing', key: 'land.nav.pricing' },
  { id: 'faq', key: 'land.nav.faq' },
];

export function TopBar() {
  const { t } = useT();
  const { session } = useSession();
  const { pathname } = useLocation();
  const home = pathname === '/';
  /* the home page's bar is night in both themes, and so are the sign-up and sign-in screens: the phone's browser bar
     matches them there */
  const night = home || isNightPath(pathname);
  useEffect(() => {
    const light = document.querySelector<HTMLMetaElement>('meta[name="theme-color"][media*="light"]');
    if (light) light.content = night ? '#070B16' : '#F3F5FA';
  }, [night]);
  return (
    <header className={home ? 'topbar topbar--home night' : 'topbar'}>
      <div className="topbar__in">
        <Link to="/" className="brand"><BrandMark /><span>PathsAbroad</span></Link>
        {home && (
          <nav className="topnav" aria-label={t('land.nav.label')}>
            <ul>
              {LANDING_SECTIONS.map(s => <li key={s.id}><a href={`#${s.id}`}>{t(s.key)}</a></li>)}
              <li><a href="/planner/?demo" className="topnav__demo">{t('land.nav.demo')}</a></li>
            </ul>
          </nav>
        )}
        <span className="topbar__space" />
        <LanguagePicker />
        {session.status === 'in' && !pathname.startsWith('/account') && pathname !== WEB_ROUTES.guardianNeeded && pathname !== WEB_ROUTES.signinCode && (
          <Link to={WEB_ROUTES.account} className="topbar__link">{t('nav.account')}</Link>
        )}
        {session.status === 'out' && pathname !== WEB_ROUTES.signin && (
          <Link to={WEB_ROUTES.signin} className="topbar__link">{t('nav.signin')}</Link>
        )}
        {session.status === 'out' && home && (
          <a href={WEB_ROUTES.planner} className="btn btn--primary btn--small topbar__cta">{t('land.getStarted')}</a>
        )}
        {/* a signed-in student sees the home page too, with their plan one tap away */}
        {session.status === 'in' && home && (
          <a href={WEB_ROUTES.planner} className="btn btn--primary btn--small topbar__cta">{t('nav.planner')}</a>
        )}
      </div>
    </header>
  );
}

/* English, العربية, हिन्दी, Indonesia: each name in its own script. A native select, so it works with a keyboard, a screen
   reader and a phone's own picker, and it's as wide as its longest name, so switching doesn't move the bar. Indonesian is
   "Indonesia" here, as phones and CLDR name it, so the bar still fits a 360px phone; the forms say "Bahasa Indonesia". */
const PICKER_NAME: Partial<Record<Lang, string>> = { id: 'Indonesia' };
export function LanguagePicker() {
  const { t, lang, setLang } = useT();
  return (
    <span className="langsel">
      <Icon.globe />
      <select value={lang} aria-label={t('lang.label')} lang={lang} dir={langDir(lang)} onChange={e => setLang(asLang(e.target.value))}>
        {LANGS.map(l => <option key={l} value={l} lang={l} dir={langDir(l)}>{PICKER_NAME[l] ?? LANG_NAMES[l]}</option>)}
      </select>
      <Icon.down />
    </span>
  );
}

export function Footer() {
  const { t } = useT();
  return (
    <footer className="footer">
      <div className="footer__in">
        <Link to={WEB_ROUTES.privacy}>{t('footer.privacy')}</Link>
        <Link to={WEB_ROUTES.terms}>{t('footer.terms')}</Link>
        <p className="footer__note">{t('footer.note')}</p>
      </div>
    </footer>
  );
}

export const ACCOUNT_NAV: Array<{ to: string; key: MessageKey; icon: () => ReactNode; tone: Tone }> = [
  { to: WEB_ROUTES.account, key: 'nav.overview', icon: Icon.home, tone: 'gray' },
  { to: WEB_ROUTES.profile, key: 'nav.profile', icon: Icon.person, tone: 'blue' },
  { to: WEB_ROUTES.inbox, key: 'nav.inbox', icon: Icon.inbox, tone: 'teal' },
  { to: WEB_ROUTES.plans, key: 'nav.plans', icon: Icon.pass, tone: 'orange' },
  { to: WEB_ROUTES.extension, key: 'nav.extension', icon: Icon.puzzle, tone: 'indigo' },
  { to: WEB_ROUTES.security, key: 'nav.security', icon: Icon.shield, tone: 'green' },
  { to: WEB_ROUTES.data, key: 'nav.data', icon: Icon.archive, tone: 'gray' },

  // AI: the AI guide and essay feedback

  // reminders: WhatsApp and email
  { to: WEB_ROUTES.reminders, key: 'nav.reminders', icon: Icon.bell, tone: 'red' },

  // parents: progress page and "parent pays"
  { to: WEB_ROUTES.family, key: 'nav.family', icon: Icon.people, tone: 'teal' },
];

/* a destructive row, the way iOS shows "Sign Out" */
export function SignOutRow({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" className="row row--button row--danger" onClick={onClick}>
      <span className="row__main"><span className="row__title">{label}</span></span>
    </button>
  );
}

/* The app's own navigation, the same four places as the planner's: Home, Explore and Apply live in the planner
   (/planner/#home and so on, a page load away), Account here. A sidebar on wide screens, a tab bar on phones. */
export const PORTAL_NAV: Array<{ href: string; key: MessageKey; icon: () => ReactNode; here?: boolean }> = [
  { href: `${WEB_ROUTES.planner}#home`, key: 'portal.home', icon: Icon.home },
  { href: `${WEB_ROUTES.planner}#explore`, key: 'portal.explore', icon: Icon.globe },
  { href: `${WEB_ROUTES.planner}#apply`, key: 'portal.apply', icon: Icon.list },
  { href: WEB_ROUTES.account, key: 'portal.account', icon: Icon.person, here: true },
];

export function PortalNav() {
  const { t } = useT();
  return (
    <nav className="pnav" aria-label={t('portal.label')}>
      <a href={`${WEB_ROUTES.planner}#home`} className="pnav__brand"><BrandMark gid="pnav-mark" className="pnav__mark" /><span>PathsAbroad</span></a>
      <ul className="pnav__list">
        {PORTAL_NAV.map(i => (
          <li key={i.key}>
            {i.here
              ? <Link to={i.href} className="pnav__item" aria-current="page"><i.icon /><span>{t(i.key)}</span></Link>
              : <a href={i.href} className="pnav__item"><i.icon /><span>{t(i.key)}</span></a>}
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* account pages: the app's navigation beside (or under) one centered column; every page but the account's own has a
   way back to it */
export function AccountShell({ children }: { children: ReactNode }) {
  const { t } = useT();
  const { pathname } = useLocation();
  const hub = pathname === WEB_ROUTES.account;
  return (
    <div className="portal">
      <PortalNav />
      <div className="portal__main">
        <div className="portal__col">
          {!hub && <Link to={WEB_ROUTES.account} className="portal__back"><Icon.back />{t('nav.account')}</Link>}
          {children}
        </div>
      </div>
    </div>
  );
}
